import React, {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import {
  MAX_STOREFRONT_ITEM_QUANTITY,
  getProductCategory,
  getProductId,
  getProductName,
  getProductPrice,
  getProductPriceForQuantity,
  getQuantityPriceTiers,
  getProductStock,
  isProductActive,
  canBuyProduct,
  productTracksStock,
} from "../utils/productAdapter";
import { quoteProducts } from "../api/products";
import {
  storefrontIdentityKey,
  subscribeStorefrontIdentity,
} from "../config/storefrontRuntime";
import { trackStorefrontActivity } from "../utils/activityClient";
import { gastronomicRuntimeCopy } from "../utils/siteExperienceDefaults";
import { useAuth } from "./AuthContext";

const CartContext = createContext(null);
const GASTRONOMIC_COPY = gastronomicRuntimeCopy();

function cartStorageKey(identityKey = storefrontIdentityKey()) {
  return `webprofit:${identityKey || "storefront"}:cart:v1`;
}

function safeInteger(value, fallback = 1) {
  const parsed = Number.parseInt(value, 10);
  return Number.isFinite(parsed) ? parsed : fallback;
}

function getQuantityLimit(stock, stockTracked = true) {
  // Sin control de stock, la cantidad se limita sólo por el máximo técnico
  // del storefront; no por una existencia ficticia en cero.
  if (stockTracked === false) return MAX_STOREFRONT_ITEM_QUANTITY;
  const normalizedStock = safeInteger(stock, 0);
  return normalizedStock > 0 ? Math.min(normalizedStock, MAX_STOREFRONT_ITEM_QUANTITY) : 1;
}

function cartLineId(productId, variantId = "") {
  return `${String(productId || "").trim()}:${String(variantId || "base").trim() || "base"}`;
}

function normalizedStoredTiers(item) {
  return getQuantityPriceTiers({
    quantityPriceTiers: Array.isArray(item?.quantityPriceTiers)
      ? item.quantityPriceTiers
      : item?.quantityPricing?.tiers,
  });
}

function storedQuantityResolution(item, quantity, tiers = normalizedStoredTiers(item)) {
  const normalizedQuantity = Math.max(safeInteger(quantity, 1), 1);
  let appliedTier = null;
  for (const tier of tiers) {
    if (tier.minQuantity <= normalizedQuantity) appliedTier = tier;
    else break;
  }

  const tierPrice = Number(appliedTier?.unitPrice);
  const regular = Number(item?.regularUnitPrice ?? item?.price ?? item?.unitPrice);
  return {
    unitPrice: Number.isFinite(tierPrice) && tierPrice >= 0
      ? tierPrice
      : Number.isFinite(regular) && regular >= 0
        ? regular
        : 0,
    appliedTier,
  };
}

function quantityPricingForStoredItem(item, tiers, appliedTier) {
  const source = item?.quantityPricing && typeof item.quantityPricing === "object"
    ? item.quantityPricing
    : {};
  return {
    ...source,
    enabled: tiers.length > 0,
    tiers,
    applied: Boolean(appliedTier),
    appliedTier: appliedTier ? { ...appliedTier } : null,
  };
}

function normalizeStoredItem(item) {
  const productId = String(item?.productId || item?.id || "").trim();
  const variantId = String(item?.variantId || "").trim();
  const submittedPrice = Number(item?.price ?? item?.unitPrice);
  const regularUnitPrice = Number(item?.regularUnitPrice ?? item?.catalogUnitPrice ?? submittedPrice);
  const basePrice = Number(item?.basePrice ?? item?.baseUnitPrice ?? submittedPrice);
  const catalogBaseUnitPrice = Number(item?.catalogBaseUnitPrice ?? basePrice);
  const stock = safeInteger(item?.stock, 0);
  const stockTracked = item?.stockTracked !== false;
  const limit = getQuantityLimit(stock, stockTracked);
  const quantity = Math.min(Math.max(safeInteger(item?.quantity, 1), 1), limit);
  if (!productId || !Number.isFinite(submittedPrice) || submittedPrice < 0) return null;

  const quantityPriceTiers = normalizedStoredTiers(item);
  const resolution = storedQuantityResolution(
    { ...item, regularUnitPrice },
    quantity,
    quantityPriceTiers,
  );
  const price = quantityPriceTiers.length ? resolution.unitPrice : submittedPrice;
  const submittedLineTotal = Number(item?.lineTotal);

  return {
    lineId: cartLineId(productId, variantId),
    productId,
    variantId: variantId || null,
    variantSku: String(item?.variantSku || ""),
    variantLabel: String(item?.variantLabel || ""),
    variantAttributes:
      item?.variantAttributes && typeof item.variantAttributes === "object"
        ? item.variantAttributes
        : {},
    name: String(item?.name || "Producto"),
    category: String(item?.category || ""),
    price,
    unitPrice: price,
    regularUnitPrice: Number.isFinite(regularUnitPrice) ? regularUnitPrice : price,
    basePrice: Number.isFinite(basePrice) ? basePrice : price,
    baseUnitPrice: Number.isFinite(basePrice) ? basePrice : price,
    catalogBaseUnitPrice: Number.isFinite(catalogBaseUnitPrice)
      ? catalogBaseUnitPrice
      : Number.isFinite(basePrice)
        ? basePrice
        : price,
    lineTotal: Number.isFinite(submittedLineTotal) && !quantityPriceTiers.length
      ? submittedLineTotal
      : price * quantity,
    currency: String(item?.currency || "ARS").toUpperCase(),
    image: String(item?.image || ""),
    catalogOrigin: item?.catalogOrigin === "/carta" ? "/carta" : "/productos",
    stock,
    stockTracked,
    quantity,
    quantityPricing: quantityPricingForStoredItem(
      item,
      quantityPriceTiers,
      resolution.appliedTier,
    ),
    quantityPriceTiers,
  };
}


function readInitialCart(identityKey = storefrontIdentityKey()) {
  if (typeof window === "undefined") return [];
  try {
    const parsed = JSON.parse(window.localStorage.getItem(cartStorageKey(identityKey)) || "[]");
    return (Array.isArray(parsed) ? parsed : []).map(normalizeStoredItem).filter(Boolean);
  } catch {
    return [];
  }
}

function quoteRequestItems(source) {
  return source.map((item) => ({
    productId: item.productId,
    variantId: item.variantId || undefined,
    quantity: item.quantity,
  }));
}

export function CartProvider({ children }) {
  const { pricingRevision, isAuthenticated } = useAuth();
  const [identityKey, setIdentityKey] = useState(() => storefrontIdentityKey());
  const [items, setItems] = useState(() => readInitialCart(storefrontIdentityKey()));
  const [drawerOpen, setDrawerOpen] = useState(false);
  const [pricing, setPricing] = useState(null);
  const [currency, setCurrency] = useState("ARS");
  const [pricingLoading, setPricingLoading] = useState(false);
  const [pricingError, setPricingError] = useState("");
  const [pricingNotice, setPricingNotice] = useState("");
  const [promotions, setPromotions] = useState([]);
  const [promotionSavings, setPromotionSavings] = useState(0);
  const quoteVersionRef = useRef(0);

  useEffect(() => {
    try {
      window.localStorage.setItem(cartStorageKey(identityKey), JSON.stringify(items));
    } catch (error) {
      console.warn("[CartContext] No se pudo persistir el carrito:", error);
    }
  }, [identityKey, items]);

  useEffect(
    () => subscribeStorefrontIdentity((identity) => {
      const nextIdentityKey = storefrontIdentityKey(identity);
      quoteVersionRef.current += 1;
      setIdentityKey(nextIdentityKey);
      setItems(readInitialCart(nextIdentityKey));
      setPricing(null);
      setCurrency("ARS");
      setPricingError("");
      setPricingNotice("");
      setPromotions([]);
      setPromotionSavings(0);
      setPricingLoading(false);
    }),
    []
  );

  useEffect(() => {
    if (items.length) return;
    setPricing(null);
    setCurrency("ARS");
    setPricingError("");
    setPricingNotice("");
    setPromotions([]);
    setPromotionSavings(0);
  }, [items.length]);

  const refreshPricing = useCallback(async ({ itemsOverride = null, silent = false } = {}) => {
    const source = (itemsOverride || items).map(normalizeStoredItem).filter(Boolean);
    const quoteVersion = quoteVersionRef.current + 1;
    quoteVersionRef.current = quoteVersion;

    if (!source.length) {
      setItems([]);
      setPricing(null);
      setCurrency("ARS");
      setPricingError("");
      setPricingNotice("");
      setPromotions([]);
      setPromotionSavings(0);
      setPricingLoading(false);
      return { ok: true, items: [], removed: [], subtotal: 0, promotionSavings: 0, promotions: [], total: 0, currency: "ARS", pricing: null };
    }

    if (!silent) setPricingLoading(true);
    setPricingError("");
    setPromotions([]);
    setPromotionSavings(0);
    try {
      const quote = await quoteProducts(quoteRequestItems(source));
      if (quoteVersion !== quoteVersionRef.current) return quote;
      const sourceByLine = new Map(source.map((item) => [item.lineId, item]));
      const normalized = (Array.isArray(quote.items) ? quote.items : [])
        .map((item) => {
          const lineId = cartLineId(item?.productId || item?.id, item?.variantId || "");
          const previous = sourceByLine.get(lineId);
          return normalizeStoredItem({
            ...(previous || {}),
            ...item,
            catalogOrigin: previous?.catalogOrigin || item?.catalogOrigin,
          });
        })
        .filter(Boolean);
      setItems(normalized);
      setPricing(quote.pricing || null);
      setCurrency(String(quote.currency || quote.pricing?.currency || "ARS").toUpperCase());
      setPromotions(Array.isArray(quote.promotions) ? quote.promotions : []);
      setPromotionSavings(Math.max(0, Number(quote.promotionSavings || 0)));
      const removed = Array.isArray(quote.removed) ? quote.removed : [];
      const cartaContext = source.some((item) => item?.catalogOrigin === "/carta");
      setPricingNotice(
        removed.length
          ? cartaContext
            ? GASTRONOMIC_COPY.cartRemovedUnavailable
            : removed.map((item) => item.message).filter(Boolean).join(" ") || "Se quitaron publicaciones que ya no estaban disponibles."
          : ""
      );
      return { ...quote, items: normalized };
    } catch (error) {
      if (quoteVersion !== quoteVersionRef.current) throw error;
      setPricingError(
        source.some((item) => item?.catalogOrigin === "/carta")
          ? GASTRONOMIC_COPY.cartPricingError
          : error?.message || "No se pudieron validar los precios del carrito.",
      );
      throw error;
    } finally {
      if (quoteVersion === quoteVersionRef.current) setPricingLoading(false);
    }
  }, [items]);

  const quoteKey = useMemo(
    () => items.map((item) => `${item.lineId}:${item.quantity}`).sort().join("|"),
    [items]
  );

  useEffect(() => {
    if (!items.length) return;
    refreshPricing({ silent: false }).catch(() => {});
    // quoteKey excluye los importes para que la respuesta del servidor no genere un bucle.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [quoteKey, pricingRevision, isAuthenticated]);

  useEffect(() => {
    const refresh = () => {
      if (items.length) refreshPricing({ silent: true }).catch(() => {});
    };
    const onVisibility = () => {
      if (document.visibilityState === "visible") refresh();
    };
    window.addEventListener("focus", refresh);
    document.addEventListener("visibilitychange", onVisibility);
    return () => {
      window.removeEventListener("focus", refresh);
      document.removeEventListener("visibilitychange", onVisibility);
    };
  }, [items.length, refreshPricing]);

  const addItem = useCallback((product, quantity = 1, options = {}) => {
    setPromotions([]);
    setPromotionSavings(0);
    const productId = String(getProductId(product) || "").trim();
    const variant = options?.variant || null;
    const fromCarta = options?.catalogOrigin === "/carta";
    const name = getProductName(product) || (fromCarta ? "Opción" : "Producto");
    const variantId = String(variant?.id || variant?._id || "").trim();
    const lineId = cartLineId(productId, variantId);
    const price = getProductPrice(product, variant);
    const regularUnitPrice = price;
    const quantityPriceTiers = getQuantityPriceTiers(product, variant);
    const basePrice = Number(variant?.basePrice ?? product?.basePrice ?? price);
    const stock = getProductStock(product, variant);
    const stockTracked = productTracksStock(product);
    const requested = Math.max(safeInteger(quantity, 1), 1);

    if (!productId) return { ok: false, message: fromCarta ? GASTRONOMIC_COPY.invalidId : "El producto no tiene un identificador válido." };
    if (!isProductActive(product)) return { ok: false, message: fromCarta ? GASTRONOMIC_COPY.unavailable : "Esta publicación no está disponible." };
    if (!canBuyProduct(product, variant)) {
      return { ok: false, message: "Este servicio o publicación requiere consulta directa." };
    }
    if (stockTracked && stock <= 0) return { ok: false, message: fromCarta ? GASTRONOMIC_COPY.noStock : "Este producto no tiene stock disponible." };

    let result = { ok: true, quantity: requested, message: fromCarta ? GASTRONOMIC_COPY.cartAdded : "Producto agregado al carrito." };
    setItems((current) => {
      const existing = current.find((item) => item.lineId === lineId);
      const requestedTotal = (existing?.quantity || 0) + requested;
      const nextQuantity = Math.min(requestedTotal, getQuantityLimit(stock, stockTracked));
      const reachedLimit = nextQuantity < requestedTotal;
      const quantityUnitPrice = getProductPriceForQuantity(product, variant, nextQuantity);
      const nextItem = normalizeStoredItem({
        lineId,
        productId,
        variantId: variantId || null,
        variantSku: variant?.sku || "",
        variantLabel: options?.variantLabel || variant?.label || "",
        variantAttributes: variant?.attributes || {},
        name,
        category: getProductCategory(product),
        price: quantityUnitPrice,
        regularUnitPrice,
        basePrice,
        currency: variant?.currency || product?.currency || product?.pricing?.currency || "ARS",
        image: options?.image || existing?.image || "",
        catalogOrigin: options?.catalogOrigin === "/carta" || existing?.catalogOrigin === "/carta" ? "/carta" : "/productos",
        stock,
        stockTracked,
        quantity: nextQuantity,
        quantityPricing: variant?.quantityPricing || product?.quantityPricing || null,
        quantityPriceTiers,
      });
      result = {
        ok: true,
        quantity: nextQuantity,
        message: reachedLimit ? (fromCarta ? GASTRONOMIC_COPY.cartMaxAvailable : "Se agregó el máximo disponible de este producto.") : (fromCarta ? GASTRONOMIC_COPY.cartAdded : "Producto agregado al carrito."),
      };
      return existing
        ? current.map((item) => (item.lineId === lineId ? nextItem : item))
        : [...current, nextItem];
    });

    trackStorefrontActivity("cart_add", {
      productId,
      productName: name,
      category: getProductCategory(product),
      quantity: requested,
      variantId: variantId || null,
      unitPrice: price,
    }, { dedupeMs: 500 });
    return result;
  }, []);

  const updateQuantity = useCallback((lineId, quantity) => {
    setPromotions([]);
    setPromotionSavings(0);
    const currentItem = items.find((item) => item.lineId === lineId || item.productId === lineId);
    setItems((current) => current.map((item) => {
      if (item.lineId !== lineId && item.productId !== lineId) return item;
      const nextQuantity = Math.min(
        Math.max(safeInteger(quantity, 1), 1),
        getQuantityLimit(item.stock, item.stockTracked !== false),
      );
      const resolution = storedQuantityResolution(item, nextQuantity);
      const nextPrice = resolution.unitPrice;
      return {
        ...item,
        quantity: nextQuantity,
        price: nextPrice,
        unitPrice: nextPrice,
        lineTotal: nextPrice * nextQuantity,
        quantityPricing: quantityPricingForStoredItem(
          item,
          normalizedStoredTiers(item),
          resolution.appliedTier,
        ),
      };
    }));
    if (currentItem) {
      trackStorefrontActivity("cart_quantity_change", {
        productId: currentItem.productId,
        productName: currentItem.name,
        quantity: safeInteger(quantity, 1),
        variantId: currentItem.variantId,
      }, { dedupeMs: 400 });
    }
  }, [items]);

  const removeItem = useCallback((lineId) => {
    setPromotions([]);
    setPromotionSavings(0);
    const currentItem = items.find((item) => item.lineId === lineId || item.productId === lineId);
    setItems((current) => current.filter((item) => item.lineId !== lineId && item.productId !== lineId));
    if (currentItem) {
      trackStorefrontActivity("cart_remove", {
        productId: currentItem.productId,
        productName: currentItem.name,
        variantId: currentItem.variantId,
      }, { dedupeMs: 400 });
    }
  }, [items]);

  const clearCart = useCallback(() => {
    quoteVersionRef.current += 1;
    setItems([]);
    setPricing(null);
    setCurrency("ARS");
    setPricingError("");
    setPricingNotice("");
    setPromotions([]);
    setPromotionSavings(0);
  }, []);
  const openCart = useCallback(() => setDrawerOpen(true), []);
  const closeCart = useCallback(() => setDrawerOpen(false), []);
  const toggleCart = useCallback(() => setDrawerOpen((open) => !open), []);

  const cartCount = useMemo(() => items.reduce((sum, item) => sum + item.quantity, 0), [items]);
  const subtotal = useMemo(
    () => items.reduce((sum, item) => sum + Number(item.lineTotal ?? item.price * item.quantity), 0),
    [items]
  );
  const total = useMemo(() => Math.max(0, subtotal - promotionSavings), [subtotal, promotionSavings]);

  const value = useMemo(() => ({
    items,
    cartCount,
    subtotal,
    promotionSavings,
    promotions,
    total,
    currency,
    pricing,
    pricingSignature: pricing?.signature || "",
    pricingLoading,
    pricingError,
    pricingNotice,
    isPricingReady: !pricingLoading && !pricingError,
    isEmpty: items.length === 0,
    drawerOpen,
    addItem,
    updateQuantity,
    removeItem,
    clearCart,
    refreshPricing,
    openCart,
    closeCart,
    toggleCart,
  }), [
    items, cartCount, subtotal, promotionSavings, promotions, total, currency, pricing, pricingLoading, pricingError,
    pricingNotice, drawerOpen, addItem, updateQuantity, removeItem, clearCart,
    refreshPricing, openCart, closeCart, toggleCart,
  ]);

  return <CartContext.Provider value={value}>{children}</CartContext.Provider>;
}

export function useCart() {
  const context = useContext(CartContext);
  if (!context) throw new Error("useCart debe usarse dentro de <CartProvider />");
  return context;
}
